// App controller: one check at a time, idle → testing → result | error.
import { analyze, type Analysis } from './analysis';
import { isMockScenario, MockProvider } from './measurement/mock';
import { CloudflareWorkersProvider } from './measurement/provider';
import {
  MeasurementError,
  toMeasurementError,
  type MeasurementProvider,
  type RawMeasurementResult,
  type TestStage,
} from './measurement/types';
import { renderDebug, renderError, renderResult, resetStages, showStage, showView } from './ui/view';

/** Generous: very slow links (~1 Mbps) need ~40 s. */
const TEST_TIMEOUT_MS = 75_000;

const params = new URLSearchParams(location.search);
const isLocalHost = /^(localhost|127\.0\.0\.1|\[::1\])$|\.localhost$/.test(location.hostname);
const debug = params.has('debug');
const mockName = params.get('mock');

// Mock scenarios exist for UI development and are only honoured on local hosts.
const provider: MeasurementProvider =
  isLocalHost && mockName && isMockScenario(mockName) ? new MockProvider(mockName) : new CloudflareWorkersProvider();

const ERROR_MESSAGES: Partial<Record<MeasurementError['code'], string>> = {
  interrupted: 'The check stopped because the page went to the background. Keep this page open and try again.',
  timeout: 'The check took too long to finish. Check your connection and try again.',
};
const DEFAULT_ERROR = 'Check your connection and try again.';

let running: AbortController | null = null;

async function runCheck(): Promise<void> {
  if (running) return;
  const controller = new AbortController();
  running = controller;
  const timer = setTimeout(
    () => controller.abort(new MeasurementError('timeout', `No result after ${TEST_TIMEOUT_MS / 1000} s`)),
    TEST_TIMEOUT_MS,
  );
  // Background tabs and locked phones throttle timers and networking, which would corrupt the measurement.
  const onHidden = () => {
    if (document.visibilityState === 'hidden') {
      controller.abort(new MeasurementError('interrupted', 'Page was hidden during the check'));
    }
  };
  document.addEventListener('visibilitychange', onHidden);

  let stage: TestStage = 'connecting';
  let raw: RawMeasurementResult | undefined;
  let analysis: Analysis | undefined;
  const startedAt = performance.now();

  resetStages();
  showView('testing');
  showStage('connecting');
  renderDebug(false, null);

  try {
    raw = await provider.startTest({
      signal: controller.signal,
      onStage: (next) => {
        stage = next;
        showStage(next);
      },
    });
    stage = 'analyzing';
    showStage('analyzing');
    analysis = analyze(raw);
    renderResult(analysis);
    showView('result');
    renderDebug(debug, debugReport({ raw, analysis, wallClockMs: performance.now() - startedAt }));
  } catch (error) {
    const failure = toMeasurementError(error, stage);
    if (failure.code === 'cancelled') {
      showView('home');
    } else {
      renderError(ERROR_MESSAGES[failure.code] ?? DEFAULT_ERROR);
      showView('error');
      renderDebug(
        debug,
        debugReport({
          raw,
          error: { code: failure.code, message: failure.message, stage: failure.stage ?? stage, details: failure.details },
          wallClockMs: performance.now() - startedAt,
        }),
      );
    }
  } finally {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHidden);
    running = null;
  }
}

function debugReport(data: Record<string, unknown>): Record<string, unknown> {
  return {
    provider: provider.name,
    endpoint: location.origin,
    userAgent: navigator.userAgent,
    at: new Date().toISOString(),
    ...data,
  };
}

function cancelCheck(): void {
  running?.abort(new MeasurementError('cancelled', 'Cancelled by the user'));
}

function init(): void {
  document.getElementById('start')?.addEventListener('click', () => void runCheck());
  document.getElementById('again')?.addEventListener('click', () => void runCheck());
  document.getElementById('retry')?.addEventListener('click', () => void runCheck());
  document.getElementById('cancel')?.addEventListener('click', cancelCheck);
  if (debug) renderDebug(true, { provider: provider.name, endpoint: location.origin, note: 'Run a check to see details.' });
}

init();
